// THIS IS AN AUTOGENERATED FILE. DO NOT EDIT THIS FILE DIRECTLY.

import { useMutation, useMutationState } from '@tanstack/react-query';
import { useHttpClient } from '@toggl/sync/http';
import type {
  Interview,
  InterviewCreateInterviewPayload,
  InterviewEvaluationPayload,
  InterviewGenerateParams,
} from '../types';
import type { MutationOptions } from '../utils/mutations';
import {
  createInterviewMutationKey,
  deleteEvaluationMutationKey,
  deleteInterviewMutationKey,
  generateInterviewAiMutationKey,
  postInterviewEvaluationMutationKey,
  putEvaluationsMutationKey,
  updateInterviewMutationKey,
} from './keys';
import {
  createInterview,
  deleteEvaluation,
  deleteInterview,
  generateInterviewAi,
  postInterviewEvaluation,
  putEvaluations,
  updateInterview,
} from './requests';

export type PostInterviewEvaluationMutationVariables = {
  workspaceId: number;
  openingId: number;
  candidateId: number;
  interviewId: number;
  data: InterviewEvaluationPayload;
};

export type PostInterviewEvaluationMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof postInterviewEvaluation,
    PostInterviewEvaluationMutationVariables,
    TContext
  >;

/**
 * Create an interview evaluation for a specific interview and candidate.
 */
export function usePostInterviewEvaluationMutation<TContext = unknown>(
  options: PostInterviewEvaluationMutationOptions<TContext> = {}
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ workspaceId, openingId, candidateId, interviewId, data }) =>
      postInterviewEvaluation(
        httpClient,
        workspaceId,
        openingId,
        candidateId,
        interviewId,
        data
      ),
    mutationKey: postInterviewEvaluationMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending postInterviewEvaluation mutation. */
export function usePostInterviewEvaluationMutationVariables() {
  return useMutationState({
    filters: {
      mutationKey: postInterviewEvaluationMutationKey,
      status: 'pending',
    },
    select: (mutation) =>
      mutation.state.variables as PostInterviewEvaluationMutationVariables,
  });
}

export type PutEvaluationsMutationVariables = {
  workspaceId: number;
  openingId: number;
  candidateId: number;
  interviewId: number;
  evaluationId: number;
  data: InterviewEvaluationPayload;
};

export type PutEvaluationsMutationOptions<TContext = unknown> = MutationOptions<
  typeof putEvaluations,
  PutEvaluationsMutationVariables,
  TContext
>;

/**
 * Atomically replace the evaluation for an interview & candidate.
 */
export function usePutEvaluationsMutation<TContext = unknown>(
  options: PutEvaluationsMutationOptions<TContext> = {}
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({
      workspaceId,
      openingId,
      candidateId,
      interviewId,
      evaluationId,
      data,
    }) =>
      putEvaluations(
        httpClient,
        workspaceId,
        openingId,
        candidateId,
        interviewId,
        evaluationId,
        data
      ),
    mutationKey: putEvaluationsMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending putEvaluations mutation. */
export function usePutEvaluationsMutationVariables() {
  return useMutationState({
    filters: { mutationKey: putEvaluationsMutationKey, status: 'pending' },
    select: (mutation) =>
      mutation.state.variables as PutEvaluationsMutationVariables,
  });
}

export type DeleteEvaluationMutationVariables = {
  workspaceId: number;
  openingId: number;
  candidateId: number;
  interviewId: number;
  evaluationId: number;
};

export type DeleteEvaluationMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof deleteEvaluation,
    DeleteEvaluationMutationVariables,
    TContext
  >;

/**
 * Remove a specific evaluation for an interview + candidate.
 */
export function useDeleteEvaluationMutation<TContext = unknown>(
  options: DeleteEvaluationMutationOptions<TContext> = {}
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({
      workspaceId,
      openingId,
      candidateId,
      interviewId,
      evaluationId,
    }) =>
      deleteEvaluation(
        httpClient,
        workspaceId,
        openingId,
        candidateId,
        interviewId,
        evaluationId
      ),
    mutationKey: deleteEvaluationMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending deleteEvaluation mutation. */
export function useDeleteEvaluationMutationVariables() {
  return useMutationState({
    filters: { mutationKey: deleteEvaluationMutationKey, status: 'pending' },
    select: (mutation) =>
      mutation.state.variables as DeleteEvaluationMutationVariables,
  });
}

export type UpdateInterviewMutationVariables = {
  workspaceId: number;
  openingId: number;
  stageId: number;
  data: Interview;
};

export type UpdateInterviewMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof updateInterview,
    UpdateInterviewMutationVariables,
    TContext
  >;

/**
 * Updates an interview and returns it
 */
export function useUpdateInterviewMutation<TContext = unknown>(
  options: UpdateInterviewMutationOptions<TContext> = {}
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ workspaceId, openingId, stageId, data }) =>
      updateInterview(httpClient, workspaceId, openingId, stageId, data),
    mutationKey: updateInterviewMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending updateInterview mutation. */
export function useUpdateInterviewMutationVariables() {
  return useMutationState({
    filters: { mutationKey: updateInterviewMutationKey, status: 'pending' },
    select: (mutation) =>
      mutation.state.variables as UpdateInterviewMutationVariables,
  });
}

export type CreateInterviewMutationVariables = {
  workspaceId: number;
  openingId: number;
  stageId: number;
  data: InterviewCreateInterviewPayload;
};

export type CreateInterviewMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof createInterview,
    CreateInterviewMutationVariables,
    TContext
  >;

/**
 * Creates an interview and returns it
 */
export function useCreateInterviewMutation<TContext = unknown>(
  options: CreateInterviewMutationOptions<TContext> = {}
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ workspaceId, openingId, stageId, data }) =>
      createInterview(httpClient, workspaceId, openingId, stageId, data),
    mutationKey: createInterviewMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending createInterview mutation. */
export function useCreateInterviewMutationVariables() {
  return useMutationState({
    filters: { mutationKey: createInterviewMutationKey, status: 'pending' },
    select: (mutation) =>
      mutation.state.variables as CreateInterviewMutationVariables,
  });
}

export type DeleteInterviewMutationVariables = {
  workspaceId: number;
  openingId: number;
  stageId: number;
};

export type DeleteInterviewMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof deleteInterview,
    DeleteInterviewMutationVariables,
    TContext
  >;

/**
 * Deletes an interview for a specific pipeline stage
 */
export function useDeleteInterviewMutation<TContext = unknown>(
  options: DeleteInterviewMutationOptions<TContext> = {}
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ workspaceId, openingId, stageId }) =>
      deleteInterview(httpClient, workspaceId, openingId, stageId),
    mutationKey: deleteInterviewMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending deleteInterview mutation. */
export function useDeleteInterviewMutationVariables() {
  return useMutationState({
    filters: { mutationKey: deleteInterviewMutationKey, status: 'pending' },
    select: (mutation) =>
      mutation.state.variables as DeleteInterviewMutationVariables,
  });
}

export type GenerateInterviewAiMutationVariables = {
  workspaceId: number;
  openingId: number;
  stageId: number;
  data: InterviewGenerateParams;
};

export type GenerateInterviewAiMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof generateInterviewAi,
    GenerateInterviewAiMutationVariables,
    TContext
  >;

/**
 * Uses LLM to generate content for an interview and returns the content
 */
export function useGenerateInterviewAiMutation<TContext = unknown>(
  options: GenerateInterviewAiMutationOptions<TContext> = {}
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ workspaceId, openingId, stageId, data }) =>
      generateInterviewAi(httpClient, workspaceId, openingId, stageId, data),
    mutationKey: generateInterviewAiMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending generateInterviewAi mutation. */
export function useGenerateInterviewAiMutationVariables() {
  return useMutationState({
    filters: { mutationKey: generateInterviewAiMutationKey, status: 'pending' },
    select: (mutation) =>
      mutation.state.variables as GenerateInterviewAiMutationVariables,
  });
}
